import { GroupFieldLayout, RadioGroup, RadioGroupItem } from '@ardoq/forms';
import { type I18nLocale, t } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { Box } from '@ardoq/layout';
import { InlineText } from '@ardoq/typography';
import type {
  RuleBasedEditFieldColumn,
  RuleBasedEditFieldValue,
  BulkEditFieldValueSelection,
} from '../../types';
import {
  BulkEditFieldValueInput,
  formatDefaultValue,
} from './BulkEditFieldValueInput';

type BulkEditValueModeSelectorProps = {
  column: RuleBasedEditFieldColumn;
  selection: BulkEditFieldValueSelection;
  /**
   * Only on the dialog's first render. Focusing the value input when the user
   * changes the field would move focus off the field select they are still on
   * (WCAG 3.2.2).
   */
  shouldAutoFocusValueInput: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
  onModeChange: (mode: BulkEditFieldValueSelection['mode']) => void;
  onValueChange: (value: RuleBasedEditFieldValue) => void;
};

/**
 * Which of the three things the edit does: write a value the user gives, reset
 * the field to its own default, or clear it. The default is only offered where
 * the field has one to name, and the value input belongs to its own mode.
 */
export const BulkEditValueModeSelector = ({
  column,
  selection,
  shouldAutoFocusValueInput,
  i18nLocale,
  locale,
  onModeChange,
  onValueChange,
}: BulkEditValueModeSelectorProps) => (
  <RadioGroup
    groupFieldLayout={GroupFieldLayout.VERTICAL}
    label={t(i18nLocale, 'inventory:bulkEditField.valueLabel', 'New value')}
    popoverHelpContent={t(
      i18nLocale,
      'inventory:bulkEditField.valueHelp',
      'The value that will be set on every affected component.'
    )}
    value={selection.mode}
    onValueChange={mode =>
      onModeChange(mode as BulkEditFieldValueSelection['mode'])
    }
  >
    <RadioGroupItem value="value">
      {t(i18nLocale, 'inventory:bulkEditField.modeValue', 'Set a value')}
    </RadioGroupItem>
    {/* Sits under its own option: below the whole group it reads as
        belonging to whichever option comes last. */}
    {selection.mode === 'value' && (
      <Box width="full">
        <BulkEditFieldValueInput
          key={column.fieldName}
          column={column}
          value={selection.value}
          shouldAutoFocus={shouldAutoFocusValueInput}
          i18nLocale={i18nLocale}
          locale={locale}
          onValueChange={onValueChange}
        />
      </Box>
    )}
    {column.defaultValue !== null && (
      <RadioGroupItem value="default">
        {t(
          i18nLocale,
          'inventory:bulkEditField.modeDefault',
          'Set to the field default'
        )}{' '}
        <InlineText color="textSubtle">
          ({formatDefaultValue(column, i18nLocale, locale)})
        </InlineText>
      </RadioGroupItem>
    )}
    <RadioGroupItem value="empty">
      {t(i18nLocale, 'inventory:bulkEditField.modeEmpty', 'Clear the value')}
    </RadioGroupItem>
  </RadioGroup>
);
